import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, tap} from 'rxjs';
import {environment} from '../../environments/local/environment';
import {IUser} from '../models/userinfo';
import {APP_CONFIGS} from '../models/appmenu';
import {PlanningToolService} from './planning-tool.service';
import {UtilService} from './util.service';
import {TranslateService} from "@ngx-translate/core";
import {AppActionItem, ApplicationDTO, ApplicationIds} from "ngx-heka-design-lib";

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  private _applicationsSubject = new BehaviorSubject<ApplicationDTO[]>([]);
  private sharedProfile = new BehaviorSubject<IUser | null>(null);
  private hasApplicationAccess = true;

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private planningToolService: PlanningToolService,
    private utilService: UtilService
  ) {
 }

  get applicationsSubject(): BehaviorSubject<ApplicationDTO[]> {
    return this._applicationsSubject;
  }

  public getOwnUser(): Observable<IUser> {
    return this.http
      .get<IUser>(
        environment.backend.endpoint.host +
        environment.users.endpoint.paths.prefix +
        environment.users.endpoint.paths.profile
      )
      .pipe(
        tap(user => {
          if (user?.applications) {
            this._applicationsSubject.next(user.applications);
          } else {
            this._applicationsSubject.next([]);
          }
        })
      );
  }

  getSharedProfile(observableOnly: boolean = false) {
    if (this.sharedProfile.value === null && !observableOnly) {
      this.updateProfile();
    }
    return this.sharedProfile.asObservable();
  }

  resetSharedProfile(): void {
    this.sharedProfile.next(null);
  }

  updateProfile() {
    this.getOwnUser().subscribe({
      next: user => {
        this.sharedProfile.next(user);
      },
      error: err => {
        this.sharedProfile.next(null);
        this.sharedProfile.error(err);
      }
    });
  }

  generateApplicationMenu(applications: ApplicationDTO[]) {
    let appMenuItems: AppActionItem[] = []
    const unsortedMenuItems: AppActionItem[] = [];
    applications?.forEach(application => {
      const foundAppConfig = APP_CONFIGS.find(i => i.appId === application.id);
      if (foundAppConfig) {
        let appMenuItem = new AppActionItem(foundAppConfig, this.translateService.instant(foundAppConfig.translationTitle));
        this.setAppActionItemClickAction(appMenuItem);
        unsortedMenuItems.push(appMenuItem);
      }
    });

    appMenuItems = unsortedMenuItems.sort((a, b) => a.config.order - b.config.order);
    return appMenuItems
  }

  setAppActionItemClickAction(appMenuItem: AppActionItem) {
    switch (appMenuItem.config.appId) {
      case ApplicationIds.CUSTOMER_PORTAL:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.hcp.host);
        };
        break;
      case ApplicationIds.HPLUS_SERVICE:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.mh2.host);
        };
        break;
      case ApplicationIds.LEVEL_2:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.l2.host);
        };
        break;
      case ApplicationIds.ELECTRICITY_CALCULATION_TOOL:
        appMenuItem.clickHandler = () => {
          this.planningToolService.openTool(ApplicationIds.ELECTRICITY_CALCULATION_TOOL);
        };
        break;
      case ApplicationIds.TENDER_TOOL:
        appMenuItem.clickHandler = () => {
          this.planningToolService.openTool(ApplicationIds.TENDER_TOOL);
        };
        break;
      case ApplicationIds.HPLUS_DONGLE:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.dongle.host);
        };
        break;
      case ApplicationIds.HPLUS_DONGLE_ADMIN:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.dongle_admin.host);
        };
        break;
      case ApplicationIds.REMOTE_ADMIN:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.remote_admin);
        };
        break;
      case ApplicationIds.REMOTE_DESKTOP:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.remote_desktop);
        };
        break;
      case ApplicationIds.OMS_TOOL:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.redirect.host);
        };
        break;
      case ApplicationIds.STATUS:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.status);
        };
        break;
      case ApplicationIds.GO_APP_ADMIN:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.go.host);
        };
        break;
      case ApplicationIds.OPERATOR:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.hpo.host);
        };
        break;
      case ApplicationIds.B2B_ONLINE:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.b2b.host);
        };
        break;
        case ApplicationIds.LEARN_WORLD:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.learn_world.host);
        };
        break;
        case ApplicationIds.AI_PLAN:
        appMenuItem.clickHandler = () => {
          this.utilService.goToLink(environment.links.applications.ai_plan);
        };
        break;
    }
  }

  setAppAccess(hasApplicationAccess: boolean) {
    this.hasApplicationAccess = hasApplicationAccess;
  }

  hasAppAccess() {
    return this.hasApplicationAccess;
  }
}
